import ButtonBase from '@mui/material/ButtonBase';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

export interface IndividualToolCallProps {
	toolName: string;
	args: unknown;
	result: unknown;
	isError: boolean;
	status: 'running' | 'complete' | 'incomplete' | 'requires-action';
	/** Human-readable annotation the tool produced for this call, if any. */
	detail?: string;
	/** Human-readable name the tool declared for itself; shown instead of the raw name. */
	displayName?: string;
}

/**
 * Expandable console row for a single tool call. Labelled with the tool's
 * display name when it declared one — the raw name stays available in the
 * expanded panel — and the raw name otherwise.
 *
 * @param toolName - Name of the tool.
 * @param args - Arguments passed to the tool.
 * @param result - Return value (undefined while running).
 * @param isError - Whether the call ended in an error.
 * @param status - Current execution status.
 * @param detail - Human-readable annotation the tool produced for this call.
 * @param displayName - Human-readable name the tool declared for itself.
 */
export function IndividualToolCall({
	toolName,
	args,
	result,
	isError,
	status,
	detail,
	displayName,
}: IndividualToolCallProps) {
	const [expanded, setExpanded] = useState(false);
	const argsText = useMemo(() => formatPayload(args), [args]);
	const resultText = useMemo(() => formatPayload(result), [result]);
	const label = displayName !== undefined && displayName.length > 0 ? displayName : toolName;

	return (
		<div>
			<ButtonBase
				onClick={() => setExpanded((v) => !v)}
				className='flex w-full items-center justify-start gap-1.5 text-left'
			>
				<span className='shrink-0 font-mono text-sm text-green-11 select-none'>{expanded ? '▾' : '▸'}</span>
				<span className={clsx('min-w-0 shrink-0 font-mono text-sm', isError ? 'text-red-11' : 'text-blue-11')}>
					{label}
				</span>
				{detail !== undefined && detail.length > 0 && (
					<span className='truncate min-w-0 font-mono text-sm text-gray-a9'>{detail}</span>
				)}
				{status === 'running' && (
					<span className='mt-1 ml-1 inline-block h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-gray-a8' />
				)}
				{isError && <span className='shrink-0 font-mono text-sm text-red-11 uppercase'>error</span>}
			</ButtonBase>
			{expanded && (
				<div className='mt-2 space-y-1.5 border-l-2 border-gray-a4 bg-gray-a2 px-3 py-2 font-mono text-sm text-gray-a9'>
					{label !== toolName && (
						<div>
							<div className='font-semibold text-gray-a7 uppercase'>tool</div>
							<div className='mt-0.5 text-gray-a11'>{toolName}</div>
						</div>
					)}
					{argsText.length > 0 && (
						<div>
							<div className='font-semibold text-gray-a7 uppercase'>args</div>
							<pre className='mt-0.5 max-h-48 overflow-auto break-words whitespace-pre-wrap text-gray-a11'>
								{argsText}
							</pre>
						</div>
					)}
					{result !== undefined ?
						<div>
							<div className='font-semibold text-gray-a7 uppercase'>result</div>
							<pre
								className={clsx(
									'mt-0.5 max-h-64 overflow-auto break-words whitespace-pre-wrap',
									isError ? 'text-red-11' : 'text-gray-a11',
								)}
							>
								{resultText}
							</pre>
						</div>
					:	status !== 'running' && <div className='text-gray-a8 italic'>(no result)</div>}
				</div>
			)}
		</div>
	);
}

/**
 * Serialises a tool argument or result value to a display string.
 *
 * @param value - The value to format.
 * @returns A human-readable string, or an empty string if the value is undefined.
 */
function formatPayload(value: unknown): string {
	if (value === undefined) {
		return '';
	}
	if (typeof value === 'string') {
		return value;
	}
	try {
		return JSON.stringify(value, null, 2);
	} catch {
		return '[unserializable value]';
	}
}
